/** Where a line in the anatomy tree opens for editing.
 *
 *  There are two targets, not one, because the two sources are edited on
 *  different screens:
 *
 *  - A delivery method's own body / subject lives in the notification editor,
 *    which selects a delivery method and then a per-field accordion — so the
 *    link has to carry `dm` AND `field` AND `line`, and the editor resolves all
 *    three (see `useRevealIntent`).
 *  - A block definition is edited in a dialog on the Blocks page, reachable via
 *    the existing `?edit=<id>` intent. That dialog uses plain fields rather
 *    than the code editor, so **there is no line to reveal there** — the link
 *    opens the block and stops. Pretending otherwise would send someone looking
 *    for a cursor that never arrives.
 *
 *  A `null` return means "no console editor for this source" (a shared
 *  `Standard_Template` block): render no link rather than a broken one.
 */
import type { AnatomyEditTarget } from '@/api/notificationAnatomy';

export interface EditorLink {
  href: string;
  /** True when the link lands on the exact line; false when it only opens the
   *  record. Callers use this to word the affordance honestly. */
  exact: boolean;
  label: string;
}

export function editorLink(
  notificationId: number,
  target: AnatomyEditTarget | undefined,
  line: number | null | undefined,
): EditorLink | null {
  if (!target) return null;

  if (target.kind === 'block') {
    return {
      href: `/notifications/blocks?edit=${target.block_id}`,
      exact: false,
      label: 'Open the block',
    };
  }

  const params = new URLSearchParams({
    dm: String(target.dm_id),
    field: target.field,
  });
  if (line) params.set('line', String(line));
  return {
    href: `/notifications/${notificationId}?${params.toString()}`,
    exact: Boolean(line),
    label: line ? `Open line ${line} in the editor` : 'Open in the editor',
  };
}
